import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import {
  IDeliveryAddress,
  IUpdateDeliveryAddressInput,
  useUpdateDeliveryAddressMutation,
} from 'generated/graphql-gateway';
import { StoreLocatorSteps } from 'pages/store-locator/types';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { toast } from 'utils/toast';

interface UseEditDeliveryAddressParams {
  setCurrentStep: (step: StoreLocatorSteps) => void;
  setEditedAddress: (address: IDeliveryAddress) => void;
}

const createAddressLine = (address: IDeliveryAddress): string => {
  return `${address.route} ${address.streetNumber}`;
};

const mapValuesToUpdateDTO = (
  values: Omit<IDeliveryAddress, '__typename'>
): IUpdateDeliveryAddressInput => {
  return {
    ...values,
    alias: values.alias ?? '',
    addressLine1: createAddressLine(values),
  };
};

export const useEditDeliveryAddress = ({
  setCurrentStep,
  setEditedAddress,
}: UseEditDeliveryAddressParams) => {
  const { formatMessage } = useIntl();
  const { refreshCurrentUser } = useAuthContext();
  const { trackEvent } = useCdpContext();

  const [updateDeliveryAddressMutation, { loading, error }] = useUpdateDeliveryAddressMutation({
    onCompleted: refreshCurrentUser,
    onError: refreshCurrentUser,
  });

  const updateDeliveryAddress = useCallback(
    (values: Omit<IDeliveryAddress, '__typename'>) => {
      updateDeliveryAddressMutation({
        variables: {
          input: mapValuesToUpdateDTO(values),
        },
      }).then(({ errors }) => {
        if (!errors) {
          toast.success(formatMessage({ id: 'deliveryAddressUpdatedSuccessfully' }));
          setEditedAddress(values);
          setCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR);

          trackEvent({
            name: CustomEventNames.EDIT_DELIVERY_ADDRESS,
            type: EventTypes.UserPreference,
            attributes: values,
          });
        } else {
          toast.error(formatMessage({ id: 'errorToUpdateDeliveryAddress' }));
        }
      });
    },
    [formatMessage, setCurrentStep, updateDeliveryAddressMutation, trackEvent, setEditedAddress]
  );

  return {
    error,
    loading,
    updateDeliveryAddress,
  };
};
